import clsx from 'clsx'
import { type HTMLAttributes, useState } from 'react'

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  src?: string | null
  /** Used for alt text when src is present and for initials fallback. */
  name: string
  size?: 'sm' | 'md' | 'lg'
}

const sizeClass: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'h-6 w-6 text-[11px]',
  md: 'h-8 w-8 text-[12px]',
  lg: 'h-10 w-10 text-[14px]',
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase()
}

export function Avatar({ src, name, size = 'md', className, ...rest }: AvatarProps) {
  const [errored, setErrored] = useState(false)
  const showImg = src && !errored
  return (
    <span
      role="img"
      aria-label={name}
      className={clsx(
        'inline-flex items-center justify-center overflow-hidden rounded-full',
        'bg-[var(--bg-3)] text-[var(--text)] font-medium select-none',
        'border border-[var(--border)]',
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      {showImg ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={src}
          alt=""
          onError={() => setErrored(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </span>
  )
}
